import React from "react";
import { Chip, ListItemButton, ListItemText, Stack, Tooltip, Typography } from "@mui/material";
import { ArrowRight } from "lucide-react";

function getStatusChipByFollowUp() {
  return {
    overdue: { label: "Overdue", bg: "hsl(0 72% 95%)", border: "hsl(0 72% 51%)", text: "hsl(0 72% 35%)" },
    today: { label: "Follow up today", bg: "hsl(38 92% 95%)", border: "hsl(38 92% 50%)", text: "hsl(38 70% 30%)" },
    tomorrow: { label: "Follow up tomorrow", bg: "hsl(38 92% 95%)", border: "hsl(38 92% 50%)", text: "hsl(38 70% 30%)" },
    sign_up_later: { label: "Sign up later", bg: "hsl(262 60% 95%)", border: "hsl(262 65% 55%)", text: "hsl(262 50% 35%)" },
    new: { label: "New", bg: "hsl(217 91% 95%)", border: "hsl(217 91% 50%)", text: "hsl(217 70% 30%)" },
  };
}

function formatFollowUpDisplay(isoValue) {
  if (!isoValue) return "No date";
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return "No date";

  const hours = date.getHours();
  const minutes = date.getMinutes();

  // If time is end-of-day (23:59) or midnight (00:00–00:59), show date only
  if ((hours === 23 && minutes === 59) || hours === 0) {
    return date.toLocaleDateString();
  }

  // Otherwise show time
  const h = hours % 12 || 12;
  const ampm = hours < 12 ? "AM" : "PM";
  const mm = String(minutes).padStart(2, "0");
  return `${h}:${mm} ${ampm}`;
}

const QueueItem = ({ profile, onSelect }) => {
  const statusChipByFollowUp = getStatusChipByFollowUp();
  const isSignUpLater = profile.last_action === "sign_up_later";
  const followUpStatus = profile.follow_up_date_status;
  const status = isSignUpLater && followUpStatus !== "overdue"
    ? statusChipByFollowUp.sign_up_later
    : (statusChipByFollowUp[followUpStatus] || statusChipByFollowUp.new);

  return (
    <ListItemButton
      onClick={() => onSelect(profile)}
      disableRipple
      sx={{
        borderRadius: "0.75rem",
        border: "1px solid hsl(140 8% 88%)",
        borderLeft: `3px solid ${status.border}`,
        backgroundColor: "#ffffff",
        px: 2.5,
        py: 2,
        mb: 1,
        alignItems: "center",
        gap: 2,
        transition: "all 0.2s ease",
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.98)", transform: "translateY(-2px)", boxShadow: "0 1px 2px rgba(16,24,40,0.12)" },
      }}
    >
      <ListItemText
        primary={
          <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
            {[profile?.first_name, profile?.last_name].filter(Boolean).join(" ").trim() || "Unnamed"}
          </Typography>
        }
        secondary={
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ mt: 0.5, fontSize: 13, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}
          >
            <Typography component="span" sx={{ fontSize: "inherit", fontFamily: "inherit", color: "inherit" }}>
              {profile.desired_title || "No desired title"}
            </Typography>
            <Typography component="span" sx={{ fontSize: "inherit", lineHeight: 1, color: "inherit" }}>
              •
            </Typography>
            <Typography component="span" sx={{ fontSize: "inherit", fontFamily: "inherit", color: "inherit" }}>
              {profile.phone || "No phone"}
            </Typography>
          </Stack>
        }
      />
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
        <Chip
          label={status.label}
          sx={{
            height: "auto",
            borderRadius: "9999px",
            backgroundColor: status.bg,
            color: status.text,
            "& .MuiChip-label": {
              display: "block",
              px: "0.625rem",
              py: "0.25rem",
              fontSize: 11,
              fontWeight: 600,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              lineHeight: 1.15,
            },
          }}
        />
        <Tooltip title="Due date" arrow>
          <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 10% 46%)" }}>
            {formatFollowUpDisplay(profile.follow_up_at)}
          </Typography>
        </Tooltip>
        <ArrowRight size={14} color="hsl(150 10% 46% / 0.4)" />
      </Stack>
    </ListItemButton>
  );
};

export default QueueItem;
